import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import { useConfirm } from '@erikdakoda/confirm-ui/useConfirm';
import { getEnvironmentTimeZone } from '@erikdakoda/i18n/shared/timezoneUtils';
import { useSnack } from '@erikdakoda/ui/hooks/useSnack';
import { useException } from '@erikdakoda/ui/hooks/useException';
import { readUserScopedItem, userScopedStorage } from '@erikdakoda/ui/shared/userScopedStorage';
import { Settings, Info, type WeekdayNumbers } from 'luxon';

const TIMEZONE_UPDATER_TIME_KEY = 'timezone-updater-last-shown';
const TIMEZONE_UPDATER_ZONE_KEY = 'timezone-updater-zone';
const HOURS_BETWEEN_PROMPTS = 3;

export default function TimezoneUpdater({ className: _className }: { className?: string }) {
  'use memo';

  const { t } = useTranslation('auth-ui');
  const { user, isUserLoading, updateCurrentUser } = useCurrentUser();
  const confirm = useConfirm();
  const { snack } = useSnack();
  const exception = useException();

  useEffect(() => {
    if (isUserLoading || !user) {
      return;
    }

    Settings.defaultWeekSettings = {
      firstDay: user.firstDayOfWeek as WeekdayNumbers,
      minimalDays: Info.getMinimumDaysInFirstWeek(),
      weekend: Info.getWeekendWeekdays(),
    };
    Settings.timeZone = user.timezone;

    // Check if we've shown the dialog recently (within configured hours)
    const lastShownTimestamp = parseInt(readUserScopedItem(TIMEZONE_UPDATER_TIME_KEY) ?? '0', 10);
    const lastShownZone = readUserScopedItem(TIMEZONE_UPDATER_ZONE_KEY);
    const now = Date.now();
    const hoursAgoThreshold = now - HOURS_BETWEEN_PROMPTS * 60 * 60 * 1000;
    const browserTimezone = getEnvironmentTimeZone();

    // Skip if we've shown the dialog recently about the same timezone
    if (lastShownTimestamp > hoursAgoThreshold && lastShownZone === browserTimezone) {
      return;
    }

    // Show dialog if browser timezone is different from user timezone
    const userTimezone = user.timezone;
    if (browserTimezone !== userTimezone) {
      showTimezoneUpdateDialog(browserTimezone, userTimezone);
    }
  }, [user, isUserLoading, confirm]);

  const showTimezoneUpdateDialog = async (browserTimezone: string, currentTimezone: string) => {
    try {
      const result = await confirm({
        title: t('updateTimezoneTitle'),
        description: (
          <>
            <div
              style={{ marginBottom: '1rem' }}
            >{`We detected that you're in ${browserTimezone}, but your profile shows ${currentTimezone}. Would you like to update your timezone?`}</div>
            <div>{`This is important for your habit reminders and notifications to come in at the correct time.`}</div>
          </>
        ),
        confirmationText: t('updateTimezoneConfirm'),
        cancellationText: t('keepCurrentTimezone'),
        dialogProps: { 'data-testid': 'timezone-update-dialog' },
        confirmationButtonProps: { 'data-testid': 'timezone-update-confirm' },
        cancellationButtonProps: { 'data-testid': 'timezone-update-cancel' },
      });

      if (result.confirmed) {
        await updateCurrentUser({ timezone: browserTimezone });
        userScopedStorage.removeItem(TIMEZONE_UPDATER_TIME_KEY);
        userScopedStorage.removeItem(TIMEZONE_UPDATER_ZONE_KEY);
        snack.success(t('timezoneUpdated', { timezone: browserTimezone }));
      } else {
        userScopedStorage.setItem(TIMEZONE_UPDATER_TIME_KEY, Date.now().toString());
        userScopedStorage.setItem(TIMEZONE_UPDATER_ZONE_KEY, browserTimezone);
      }
    } catch (error) {
      exception(error, 'Error updating timezone');
      // Mark as shown even on error to prevent repeated prompts
      userScopedStorage.setItem(TIMEZONE_UPDATER_TIME_KEY, Date.now().toString());
      userScopedStorage.setItem(TIMEZONE_UPDATER_ZONE_KEY, browserTimezone);
    }
  };

  // This component doesn't render anything visible
  return null;
}
